import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { motion } from "framer-motion";
import { Flame, LogOut, Plus, Gem, Settings, SlidersHorizontal, Users, X, MailWarning, Sun, Moon, LineChart, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import BrainLogo from "@/components/BrainLogo";
import LegalLinks from "@/components/LegalLinks";
import Confetti from "@/components/Confetti";
import ShareCardDialog from "@/components/ShareCard";
import StreakRiskBanner from "@/components/StreakRiskBanner";
import ProPassChip from "@/components/ProPassChip";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import TutorialOverlay from "@/components/TutorialOverlay";
import { useTutorial } from "@/lib/tutorial";
import { useTheme } from "@/lib/theme";
import api from "@/lib/api";
import { toast } from "sonner";

const NAV = [
  { to: "/app", label: "Home", testId: "nav-home" },
  { to: "/app/studies", label: "Studies", testId: "nav-subcourses" },
  { to: "/app/courses", label: "Courses", testId: "nav-courses" },
  { to: "/app/review", label: "Review", testId: "nav-review" },
  { to: "/app/explore", label: "Explore", testId: "nav-explore" },
  { to: "/app/awards", label: "Awards", testId: "nav-achievements" },
  { to: "/app/leaderboard", label: "Leaderboard", testId: "nav-leaderboard" },
  { to: "/app/rooms", label: "Rooms", testId: "nav-rooms" },
];

const STREAK_MILESTONES = [3, 7, 30, 100];

export default function AppShell({ children }) {
  const { user, logout, refresh } = useAuth();
  const { theme, toggle } = useTheme();
  const { startIfNew } = useTutorial();
  const loc = useLocation();
  const [celebrate, setCelebrate] = useState(false);
  const [shareCard, setShareCard] = useState(null); // {type, data}
  const [verifyBanner, setVerifyBanner] = useState(false);
  const [resending, setResending] = useState(false);
  const [dueCount, setDueCount] = useState(0);

  // Fetch count of flashcards due for review (spaced-repetition reminder badge)
  useEffect(() => {
    if (!user) return;
    let done = false;
    api.get("/flashcards/due/count")
      .then(({ data }) => { if (!done) setDueCount(data?.count || 0); })
      .catch(() => {});
    return () => { done = true; };
  }, [user?.id, loc.pathname]);

  // Daily login reward — award 1 gem once per UTC day on app open
  useEffect(() => {
    if (!user) return;
    let done = false;
    const key = `sv_daily_reward_${user.id}`;
    const todayKey = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem(key) === todayKey) return; // already claimed locally today
    api.post("/auth/daily-reward").then(({ data }) => {
      if (done) return;
      localStorage.setItem(key, todayKey);
      if (data.awarded) {
        toast.success("Daily reward — +1 gem!", { icon: "💎" });
        refresh && refresh();
      }
    }).catch(() => {});
    return () => { done = true; };
  }, [user?.id]);

  // First-time users get the gamified product tour automatically.
  useEffect(() => {
    if (!user) return;
    const t = setTimeout(() => startIfNew("essentials"), 900);
    return () => clearTimeout(t);
  }, [user?.id]);

  // Show email verification banner for unverified users (dismissed state persisted in localStorage)
  useEffect(() => {
    if (!user) return;
    if (user.email_verified === false) {
      const dismissed = localStorage.getItem(`sv_verify_dismissed_${user.id}`);
      if (!dismissed) setVerifyBanner(true);
    }
  }, [user?.email_verified, user?.id]);

  const resendVerification = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent! Check your inbox.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't resend");
    } finally { setResending(false); }
  };

  const dismissVerifyBanner = () => {
    if (user) localStorage.setItem(`sv_verify_dismissed_${user.id}`, "1");
    setVerifyBanner(false);
  };

  // Milestone confetti + share-card prompt — diff against localStorage
  useEffect(() => {
    if (!user) return;
    const lvlKey = `cognit_last_level_${user.id}`;
    const streakKey = `cognit_last_streak_${user.id}`;
    const lastLvl = parseInt(localStorage.getItem(lvlKey) || "0", 10);
    const lastStreak = parseInt(localStorage.getItem(streakKey) || "0", 10);

    if (user.level > lastLvl && lastLvl > 0) {
      setCelebrate(true);
      setTimeout(() => setCelebrate(false), 100);
      setShareCard({ type: "level", data: { value: user.level, sub: `+${user.total_xp} XP earned` } });
    }
    if (user.streak_count > lastStreak && STREAK_MILESTONES.includes(user.streak_count)) {
      setCelebrate(true);
      setTimeout(() => setCelebrate(false), 100);
      setShareCard({ type: "streak", data: { value: user.streak_count, sub: user.streak_count >= 30 ? "Locked in" : "Keep it going" } });
    }
    localStorage.setItem(lvlKey, String(user.level));
    localStorage.setItem(streakKey, String(user.streak_count));
  }, [user?.level, user?.streak_count, user?.id, user?.total_xp]);

  if (!user) return null;

  const isActive = (to) => loc.pathname === to || (to !== "/app" && loc.pathname.startsWith(to));
  const pct = user.xp_needed_for_next
    ? Math.min(100, Math.round((user.xp_into_level / user.xp_needed_for_next) * 100))
    : 0;

  const isCompose = loc.pathname === "/app/studies/new";

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-[#F4F4F5] relative flex flex-col">
      {celebrate && <Confetti active count={70} />}
      <TutorialOverlay />
      <ShareCardDialog
        open={!!shareCard}
        onClose={() => setShareCard(null)}
        type={shareCard?.type}
        data={shareCard?.data || {}}
        user={user}
      />

      <StreakRiskBanner
        user={user}
        onShare={() => setShareCard({ type: "streak", data: { value: user.streak_count, sub: "Keep it going" } })}
      />
      {/* Email verification banner */}
      {verifyBanner && (
        <div className="bg-[#1a1505] border-b border-[#F59E0B]/30 px-5 py-2.5 flex items-center justify-between gap-4"
          data-testid="email-verify-banner">
          <div className="flex items-center gap-2.5 text-[13px]">
            <MailWarning size={15} className="text-[#F59E0B] flex-shrink-0" />
            <span className="text-[#F4F4F5]">Please verify your email address.</span>
            <button onClick={resendVerification} disabled={resending}
              className="text-[#F59E0B] hover:underline font-medium disabled:opacity-50"
              data-testid="resend-verification-btn">
              {resending ? "Sending…" : "Resend email"}
            </button>
          </div>
          <button onClick={dismissVerifyBanner} className="text-[#555] hover:text-white flex-shrink-0">
            <X size={14} />
          </button>
        </div>
      )}
      <header className="glass-nav sticky top-0 z-40" data-testid="app-shell-header">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 md:px-10 h-[60px] sm:h-[64px] grid grid-cols-[auto_1fr_auto] items-center gap-3 sm:gap-6 md:gap-8">
          {/* Left — brand */}
          <Link to="/" className="flex items-center gap-2 sm:gap-2.5 group shrink-0" data-testid="logo-link">
            <BrainLogo size={28} />
            <span className="font-semibold tracking-tight text-[15px] sm:text-[16px] whitespace-nowrap">Studivault</span>
          </Link>

          {/* Center — nav pill */}
          <nav className="hidden md:flex items-center justify-center">
            <div className="inline-flex items-center gap-1 px-1.5 py-1 rounded-full bg-white/[0.04] border border-white/[0.06] backdrop-blur-md">
              {NAV.map((n) => {
                const active = isActive(n.to);
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    data-testid={n.testId}
                    className="relative px-4 py-1.5 text-[13px] font-medium rounded-full transition-colors"
                    style={{ color: active ? (theme === "light" ? "#1A1A22" : "#F4F4F5") : (theme === "light" ? "#3F3F46" : "#A1A1AA") }}
                  >
                    {active && (
                      <motion.span
                        layoutId="nav-pill"
                        className="absolute inset-0 rounded-full"
                        style={{
                          background: "linear-gradient(135deg, rgba(124, 92, 255, 0.32), rgba(90, 200, 250, 0.20))",
                          border: "1px solid rgba(124, 92, 255, 0.40)",
                          boxShadow: "0 0 26px -6px rgba(124, 92, 255, 0.60)",
                        }}
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                      />
                    )}
                    <span className="relative whitespace-nowrap">{n.label}</span>
                    {n.to === "/app/review" && dueCount > 0 && (
                      <span
                        data-testid="due-cards-badge"
                        className="absolute -top-1 -right-1 min-w-[17px] h-[17px] px-1 rounded-full bg-[#F43F5E] text-white text-[10px] font-bold flex items-center justify-center leading-none"
                        style={{ boxShadow: "0 0 10px -1px rgba(244,63,94,0.7)" }}
                        title={`${dueCount} cards due for review`}
                      >
                        {dueCount > 99 ? "99+" : dueCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Right — user */}
          <div className="flex items-center gap-2 sm:gap-3 justify-self-end">
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.06]" data-testid="streak-badge" title={`${user.streak_count}-day streak`}>
              <Flame size={13} className="text-[#B8A4FF] flame-flicker" strokeWidth={2.3} fill="#B8A4FF" />
              <span className="text-[12.5px] font-semibold text-[#F4F4F5] tabular-nums">{user.streak_count}</span>
            </div>
            <Link
              to="/app/gems"
              data-testid="gems-chip"
              title="Gem Shop"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.06] hover:border-[#B8A4FF]/40 transition-colors"
            >
              <Gem size={13} className="text-[#B8A4FF]" style={{ filter: "drop-shadow(0 0 6px rgba(167,139,250,0.85))", color: "#A78BFA" }} fill="#7C5CFF" />
              <span className="text-[12.5px] font-semibold text-white tabular-nums">{user.gems ?? 0}</span>
            </Link>
            {user.pro_pass_until ? (
              <ProPassChip until={user.pro_pass_until} />
            ) : user.plan !== "free" && (user.raw_plan || "free") === "free" ? null : (
              <Link
                to="/app/billing"
                data-testid="billing-link"
                title={user.plan === "free" ? "Upgrade" : `On ${user.plan}`}
                className={`hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.14em] transition-all ${
                  user.plan === "free"
                    ? "bg-gradient-to-r from-[#7C5CFF] to-[#5AC8FA] text-white hover:opacity-90"
                    : "bg-gradient-to-r from-[#7C5CFF]/30 to-[#5AC8FA]/20 border border-[#7C5CFF]/40 text-white"
                }`}
                style={{ boxShadow: "0 0 14px -4px rgba(124,92,255,0.55)" }}
              >
                {user.plan === "free" ? <><Sparkles size={12} /> Upgrade</> : user.plan}
              </Link>
            )}
            <div className="pl-2.5 ml-0.5 border-l border-white/[0.08]">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C5CFF] to-[#5AC8FA] flex items-center justify-center text-white text-[12.5px] font-semibold shrink-0 outline-none transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-[#7C5CFF]"
                    style={{ boxShadow: "0 0 18px -4px rgba(124,92,255,0.6)" }}
                    data-testid="profile-menu-trigger"
                    aria-label="Open profile menu"
                  >
                    {(user.full_name || "U").slice(0, 1).toUpperCase()}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  sideOffset={10}
                  className="w-60 p-1.5 rounded-2xl border shadow-xl"
                  style={{
                    background: theme === "light" ? "#FFFFFF" : "#14121d",
                    borderColor: theme === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.10)",
                    color: theme === "light" ? "#1A1A22" : "#F4F4F5",
                  }}
                  data-testid="profile-menu"
                >
                  <div className="px-2.5 py-2">
                    <div className="text-[13.5px] font-semibold truncate">{user.full_name || "Learner"}</div>
                    <div className="text-[11.5px] text-[#8E8B99] truncate">{user.email}</div>
                  </div>
                  <DropdownMenuSeparator style={{ background: theme === "light" ? "rgba(0,0,0,0.07)" : "rgba(255,255,255,0.08)" }} />

                  {user.plan === "free" && (
                    <DropdownMenuItem asChild className="px-2.5 py-2.5 rounded-xl text-[13.5px] gap-2.5 cursor-pointer focus:bg-[#7C5CFF]/25">
                      <Link to="/app/billing" data-testid="menu-upgrade" className="font-semibold text-[#B8A4FF]">
                        <Sparkles size={15} /> Upgrade to Plus
                      </Link>
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuItem
                    onSelect={(e) => { e.preventDefault(); toggle(); }}
                    className="px-2.5 py-2.5 rounded-xl text-[13.5px] gap-2.5 cursor-pointer focus:bg-[#7C5CFF]/15"
                    data-testid="menu-theme-toggle"
                  >
                    {theme === "light" ? <Moon size={15} /> : <Sun size={15} />}
                    {theme === "light" ? "Dark mode" : "Light mode"}
                  </DropdownMenuItem>

                  <DropdownMenuItem asChild className="px-2.5 py-2.5 rounded-xl text-[13.5px] gap-2.5 cursor-pointer focus:bg-[#7C5CFF]/15">
                    <Link to="/app/settings" data-testid="menu-settings"><Settings size={15} /> Settings</Link>
                  </DropdownMenuItem>

                  <DropdownMenuItem asChild className="px-2.5 py-2.5 rounded-xl text-[13.5px] gap-2.5 cursor-pointer focus:bg-[#7C5CFF]/15">
                    <Link to="/app/earnings" data-testid="menu-earnings"><LineChart size={15} /> Creator earnings</Link>
                  </DropdownMenuItem>

                  {user.email === "shaurya@sanghviinfo.com" && (
                    <DropdownMenuItem asChild className="px-2.5 py-2.5 rounded-xl text-[13.5px] gap-2.5 cursor-pointer focus:bg-[#7C5CFF]/15 text-[#B8A4FF]">
                      <Link to="/admin" data-testid="menu-admin"><SlidersHorizontal size={15} /> Admin dashboard</Link>
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuSeparator style={{ background: theme === "light" ? "rgba(0,0,0,0.07)" : "rgba(255,255,255,0.08)" }} />

                  <DropdownMenuItem
                    onSelect={(e) => { e.preventDefault(); logout(); }}
                    className="px-2.5 py-2.5 rounded-xl text-[13.5px] gap-2.5 cursor-pointer focus:bg-red-500/15 text-[#FF6B6B]"
                    data-testid="menu-logout"
                  >
                    <LogOut size={15} /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        <nav className="md:hidden border-t border-[rgba(255,255,255,0.07)] px-4 py-2 flex items-center gap-1.5 overflow-x-auto no-scrollbar">
          {NAV.map((n) => {
            const active = isActive(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`${n.testId}-m`}
                className="shrink-0 px-3.5 py-1.5 text-[13px] font-medium rounded-full whitespace-nowrap transition-colors"
                style={{
                  color: active ? (theme === "light" ? "#1A1A22" : "#F4F4F5") : (theme === "light" ? "#3F3F46" : "#A1A1AA"),
                  background: active ? "rgba(124, 92, 255, 0.20)" : "transparent",
                  border: active ? "1px solid rgba(124,92,255,0.35)" : "1px solid transparent",
                }}
              >
                <span className="relative inline-block">
                  {n.label}
                  {n.to === "/app/review" && dueCount > 0 && (
                    <span
                      className="absolute -top-1.5 -right-3 min-w-[15px] h-[15px] px-1 rounded-full bg-[#F43F5E] text-white text-[9px] font-bold flex items-center justify-center leading-none"
                      title={`${dueCount} cards due for review`}
                    >
                      {dueCount > 99 ? "99+" : dueCount}
                    </span>
                  )}
                </span>
              </Link>
            );
          })}
        </nav>
      </header>

      <motion.main
        className="flex-1"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        key={loc.pathname}
      >
        {children}
      </motion.main>

      <footer className="border-t border-white/5 px-5 md:px-8 py-8" data-testid="app-footer">
        <div className="max-w-[1320px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-[12px] text-[#8E8B99]">
          <div className="flex items-center gap-2">
            <BrainLogo size={18} />
            <span>© 2026 Studivault<span className="hidden sm:inline"> · Less cramming. More leveling up.</span></span>
          </div>
          <LegalLinks />
        </div>
      </footer>

      {!isCompose && (
        <Link to="/app/studies/new" className="fixed bottom-6 right-6 z-30" data-testid="fab-new">
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.6, type: "spring", stiffness: 280, damping: 18 }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.92 }}
            className="w-14 h-14 rounded-full bg-gradient-to-br from-[#7C5CFF] to-[#5AC8FA] flex items-center justify-center text-white shadow-2xl shadow-[#7C5CFF]/50 fab-pulse"
          >
            <Plus size={22} strokeWidth={2.2} />
          </motion.div>
        </Link>
      )}
    </div>
  );
}
